import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query, limit } from 'firebase/firestore';
import { firebaseConfigured, getFirebase } from '@/firebase';
import { MOCK_RUNS } from '@/mocks/factoryRuns';
import {
  AWAITING_APPROVAL_STATUSES,
  ARCHIVED_STATUSES,
  STAGE_ORDER,
  type FactoryRun,
  type FactoryRunStatus,
  type FactoryStage,
} from '@/lib/types';

export interface GroupedRuns {
  pinned: FactoryRun[];
  all: FactoryRun[];
  archived: FactoryRun[];
  isMock: boolean;
  loading: boolean;
  error: string | null;
}

function toMillis(ts: unknown): number | undefined {
  if (!ts) return undefined;
  if (typeof ts === 'number') return ts;
  if (typeof ts === 'object' && ts && 'toMillis' in ts) {
    const m = (ts as { toMillis: unknown }).toMillis;
    if (typeof m === 'function') {
      try {
        return (m as () => number)();
      } catch {
        return undefined;
      }
    }
  }
  return undefined;
}

function coerceStage(v: unknown): FactoryStage {
  if (typeof v === 'string' && STAGE_ORDER.includes(v as FactoryStage)) {
    return v as FactoryStage;
  }
  return 'discovery';
}

function coerceStatus(v: unknown): FactoryRunStatus {
  const s = typeof v === 'string' ? v : 'discovery';
  return s as FactoryRunStatus;
}

// Materializes a FactoryRun from a Firestore doc. This is intentionally
// permissive — the dissolver-dev factory_runs collection pre-dates this
// dashboard, so field names vary across runs. Any missing field falls back
// to a sensible default rather than throwing.
function toFactoryRun(docId: string, data: Record<string, unknown>): FactoryRun {
  const status = coerceStatus(data.status);
  const currentStage = coerceStage(
    data.current_stage ?? data.currentStage ?? data.phase ?? data.stage,
  );
  return {
    id: docId,
    status,
    currentStage,
    customerId:
      (data.customer_id as string | undefined) ??
      (data.customerId as string | undefined),
    customerName:
      (data.customer_name as string | undefined) ??
      (data.customerName as string | undefined),
    userEmail:
      (data.user_email as string | undefined) ??
      (data.userEmail as string | undefined),
    createdAtMs: toMillis(data.created_at ?? data.createdAt),
    updatedAtMs: toMillis(data.updated_at ?? data.updatedAt),
    lastMessageSnippet:
      (data.last_message_snippet as string | undefined) ??
      (data.lastMessageSnippet as string | undefined),
    unreadForFounder:
      (data.unread_for_founder as boolean | undefined) ??
      (data.unreadForFounder as boolean | undefined) ??
      false,
    awaitingApproval:
      AWAITING_APPROVAL_STATUSES.includes(status) ||
      Boolean(data.awaiting_approval ?? data.awaitingApproval),
  };
}

// Subscribes to the factory_runs collection and groups the results into
// pinned / all / archived buckets the thread list renders.
//
// Read-only. No writes to Firestore this session.
export function useFactoryRuns(): GroupedRuns {
  const [runs, setRuns] = useState<FactoryRun[]>(MOCK_RUNS);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!firebaseConfigured) {
      setLoading(false);
      setIsMock(true);
      setRuns(MOCK_RUNS);
      return;
    }
    const fb = getFirebase();
    if (!fb) {
      setLoading(false);
      setIsMock(true);
      return;
    }
    // Ordering by created_at desc. Limit is generous but capped so a runaway
    // factory doesn't blow up the UI.
    const q = query(
      collection(fb.db, 'factory_runs'),
      orderBy('created_at', 'desc'),
      limit(200),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const parsed = snap.docs.map((d) =>
          toFactoryRun(d.id, d.data() as Record<string, unknown>),
        );
        setRuns(parsed);
        setIsMock(false);
        setLoading(false);
        setError(null);
      },
      (err) => {
        // Common path in dev: permission-denied because the signed-in user
        // doesn't have Firestore rules for factory_runs. Fall back to mocks.
        setRuns(MOCK_RUNS);
        setIsMock(true);
        setLoading(false);
        setError(err.message);
      },
    );
    return () => unsub();
  }, []);

  return useMemo<GroupedRuns>(() => {
    const archived = runs.filter((r) => ARCHIVED_STATUSES.includes(r.status));
    const nonArchivedNonComplete = runs.filter(
      (r) => !ARCHIVED_STATUSES.includes(r.status) && r.status !== 'complete',
    );
    const pinned = nonArchivedNonComplete.filter(
      (r) => r.awaitingApproval || AWAITING_APPROVAL_STATUSES.includes(r.status),
    );
    // "all" per spec: every active factory_run thread, most recent first.
    // Pinned entries are included in "all" too so the founder can still find
    // a pending-approval run in the main list.
    const all = nonArchivedNonComplete
      .slice()
      .sort((a, b) => (b.updatedAtMs ?? 0) - (a.updatedAtMs ?? 0));
    return {
      pinned: pinned
        .slice()
        .sort((a, b) => (b.updatedAtMs ?? 0) - (a.updatedAtMs ?? 0)),
      all,
      archived: archived
        .slice()
        .sort((a, b) => (b.updatedAtMs ?? 0) - (a.updatedAtMs ?? 0)),
      isMock,
      loading,
      error,
    };
  }, [runs, isMock, loading, error]);
}
